import {
  EFeatureFlagComparison,
  MachineConfigUICatalogContent
} from '@clean-code/types/config';
import { FeatureFlagsType } from '@hooks/useFeatureFlags';

// Alert metric select order; Stop Time is added first by the form and unlisted types go last.
const METRIC_TYPE_ORDER: ((metricTypeName: string) => boolean)[] = [
  (name) => name === 'availability',
  (name) => name === 'speed' || name === 'cycle_speed',
  (name) => name === 'temperature',
  (name) => name.startsWith('vibration_')
];

export function metricTypeRank(metricTypeName: string): number {
  const name = metricTypeName.toLowerCase();
  const rank = METRIC_TYPE_ORDER.findIndex((matches) => matches(name));
  return rank === -1 ? METRIC_TYPE_ORDER.length : rank;
}

export const isMetricSupportedByFeatureFlags = (
  metric: MachineConfigUICatalogContent,
  featureFlags: FeatureFlagsType<boolean>
) => {
  if (metric.comparison === EFeatureFlagComparison.Or) {
    return metric.featureFlagRequired?.some((f) => featureFlags[f]) ?? false;
  }
  if (metric.comparison === EFeatureFlagComparison.And) {
    return (
      metric.featureFlagRequired?.some((f) =>
        f.every((f) => featureFlags[f])
      ) ?? false
    );
  }
  return false;
};

// Alert availability is plotted from the realtime timeline; node-amps only has raw current for it.
const AVAILABILITY_METRIC_TYPE = 'availability';

const NODE_AMPS_METRIC_TYPES = new Set([
  'corrects',
  'current',
  'cycle_count',
  'cycle_speed',
  'cycle_time',
  'total',
  'correct',
  'defects',
  'energy',
  'identity',
  'speed',
  'status',
  'temperature',
  'totals',
  'product_count',
  'vibration_displacement_x',
  'vibration_displacement_y',
  'vibration_displacement_z',
  'vibration_velocity_x',
  'vibration_velocity_y',
  'vibration_velocity_z',
  'vibration_acceleration_x',
  'vibration_acceleration_y',
  'vibration_acceleration_z',
  'pressure_left_presize_adjust_nut',
  'pressure_right_presize_adjust_nut'
]);

export function isNodeAmpsMetricType(
  metricTypeName: string | null | undefined
): boolean {
  if (!metricTypeName) return false;
  return NODE_AMPS_METRIC_TYPES.has(metricTypeName.toLowerCase());
}

export function isAvailabilityMetricType(
  metricTypeName: string | null | undefined
): boolean {
  return metricTypeName?.toLowerCase() === AVAILABILITY_METRIC_TYPE;
}

/** The realtime tab's shift-to-date OEE performance, alerted by production metric alerts. */
export const PRODUCTION_METRIC_KEY = 'oee_effectiveness';

export function isProductionMetric(metric: string | null | undefined): boolean {
  return metric === PRODUCTION_METRIC_KEY;
}
